import {
  ArrowDownWideNarrow,
  ArrowUpWideNarrow,
  Check,
  CheckCheck,
  EyeOff,
  Flag,
  Trash2,
} from 'lucide-react';
import type { InboxThread, InboxThreadListResult } from '../../types/inbox';
import type { CurrentUser } from '../../types/user';
import type { FeedSortDir } from '../../lib/inboxView';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import { formatRelativeTime } from '../../lib/relativeTime';
import { avatarInitials } from '../../components/layout/avatarInitials';
import { ChannelIcon } from '../../components/ui/ChannelIcon';
import { StarRating } from './threadDetailShared';
import { rowActionsForKind, type RowAction } from '../../lib/inboxRowActions';

// Community Inbox — middle pane: the thread list, most recent activity first.
// Phase 1.5: channel glyphs, a hover quick-resolve ✔ per row, and a
// kind-aware empty state (Reviews/Messages are Phase 2).
//
// ROW QUICK ACTIONS (2026-09-30): the hover group now carries the actions
// rowActionsForKind (src/lib/inboxRowActions.ts) allows for the thread's kind —
// comment: Hide · Delete · Resolve; review / DM: Resolve. The rule lives in that
// ONE pure function so a Delete cannot appear on a review. The buttons are
// SIBLINGS of the row's select button (never nested in it) and stop `click`
// propagation — not mousedown, which would make them inert — so an action never
// also opens the thread.

const STATUS_STYLES: Record<InboxThread['status'], string> = {
  new: 'bg-kyck-50 text-kyck-700 border-kyck-200',
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  resolved: 'bg-slate-100 text-slate-500 border-slate-200',
};

export function ThreadList({
  result,
  selectedId,
  onSelect,
  onLoadMore,
  onQuickResolve,
  onQuickHide,
  onQuickDelete,
  accountNameById,
  usersByUid,
  loading,
  emptyState,
  sortDir,
  onToggleSort,
  onBulkResolve,
}: {
  result: InboxThreadListResult | undefined;
  selectedId: string | null;
  onSelect: (thread: InboxThread) => void;
  onLoadMore: () => void;
  onQuickResolve: (thread: InboxThread) => void;
  // Comment rows only (see rowActionsForKind). The page resolves which message
  // to act on and asks for the delete confirm, exactly as the detail pane does.
  onQuickHide: (thread: InboxThread) => void;
  onQuickDelete: (thread: InboxThread) => void;
  accountNameById: Map<string, string>;
  usersByUid: Map<string, CurrentUser>;
  loading: boolean;
  emptyState: string;
  sortDir: FeedSortDir;
  onToggleSort: () => void;
  // Part D — "resolve all showing" (opens a confirm dialog with the count).
  onBulkResolve: () => void;
}) {
  const threads = result?.threads ?? [];
  const hasMore = result ? result.page * result.pageSize < result.total : false;

  return (
    // flex-1 min-h-0 so the list fills the feed column and scrolls INTERNALLY
    // (the row below is the scroll region) instead of sizing to its content and
    // pushing the page taller.
    <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden rounded-lg border border-slate-200">
      <div className="flex shrink-0 items-center justify-between border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs font-medium text-slate-500">
        <span>{result ? `${result.total} thread${result.total === 1 ? '' : 's'}` : 'Loading…'}</span>
        <span className="flex-1" />
        {/* Part D (2026-07-19) — resolve everything the current filters show. */}
        {(result?.total ?? 0) > 0 && (
          <button
            type="button"
            onClick={onBulkResolve}
            title="Resolve every item matching the current filters"
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-700"
          >
            <CheckCheck className="h-3.5 w-3.5" />
            Resolve all showing
          </button>
        )}
        {/* Part D4 — pure-recency feed with a newest ↔ oldest direction toggle. */}
        <button
          type="button"
          onClick={onToggleSort}
          title={sortDir === 'newest' ? 'Newest first (click for oldest)' : 'Oldest first (click for newest)'}
          className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-700"
        >
          {sortDir === 'newest' ? (
            <ArrowDownWideNarrow className="h-3.5 w-3.5" />
          ) : (
            <ArrowUpWideNarrow className="h-3.5 w-3.5" />
          )}
          {sortDir === 'newest' ? 'Newest' : 'Oldest'}
        </button>
      </div>
      <div className="scroll-subtle min-h-0 flex-1 overflow-y-auto">
        {threads.length === 0 && !loading && (
          <p className="px-4 py-8 text-center text-sm text-slate-500">{emptyState}</p>
        )}
        {threads.map((thread) => {
          const assignee = thread.assigneeUid ? usersByUid.get(thread.assigneeUid) : null;
          return (
            <div
              key={thread.id}
              className={cn(
                'group relative border-b border-slate-100 transition-colors hover:bg-slate-50',
                // Part A — Priority items get a clear amber accent in the list.
                thread.priority === true && 'border-l-2 border-l-amber-400 bg-amber-50/40',
                selectedId === thread.id && 'bg-kyck-50/60 hover:bg-kyck-50/60',
              )}
            >
              <button
                type="button"
                onClick={() => onSelect(thread)}
                className="block w-full px-3 py-2.5 text-left"
              >
                <div className="flex items-center gap-2">
                  <ChannelIcon channel={thread.channel} className="h-3.5 w-3.5" />
                  {thread.priority === true && (
                    <Flag className="h-3.5 w-3.5 shrink-0 fill-amber-400 text-amber-500" />
                  )}
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">
                    {thread.author.name}
                  </span>
                  {/* Part D4 — no per-row green dots / unread number badges on
                      the feed (they live only on the accounts worklist panel).
                      Review stars stay (rating, not a count). */}
                  {thread.kind === 'review' && <StarRating rating={thread.rating} />}
                  <span className="shrink-0 text-xs text-slate-400">
                    {formatRelativeTime(thread.lastActivityAt)}
                  </span>
                </div>
                <p className="mt-0.5 truncate pr-8 text-sm text-slate-500">{thread.preview}</p>
                <div className="mt-1.5 flex items-center gap-1.5">
                  <Badge
                    variant="outline"
                    className={cn('px-1.5 py-0 text-[11px]', STATUS_STYLES[thread.status])}
                  >
                    {thread.status}
                  </Badge>
                  {thread.isAd && (
                    <Badge
                      variant="outline"
                      className="border-purple-200 bg-purple-50 px-1.5 py-0 text-[11px] text-purple-700"
                    >
                      Ad
                    </Badge>
                  )}
                  <span className="min-w-0 flex-1 truncate text-[11px] text-slate-400">
                    {accountNameById.get(thread.kyckAccountId) ?? thread.kyckAccountId}
                  </span>
                  {assignee && (
                    <span
                      title={assignee.displayName || assignee.email}
                      className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-kyck-100 text-[10px] font-semibold text-kyck-700"
                    >
                      {avatarInitials(assignee.displayName, assignee.email)}
                    </span>
                  )}
                </div>
              </button>
              {/* Hover quick actions — per kind, from rowActionsForKind. Each is
                  an audited action; hide/delete route through the same calls the
                  detail pane uses. */}
              <RowActions
                actions={rowActionsForKind(thread.kind, thread.status)}
                onAction={(action) => {
                  if (action === 'resolve') onQuickResolve(thread);
                  else if (action === 'hide') onQuickHide(thread);
                  else onQuickDelete(thread);
                }}
              />
            </div>
          );
        })}
        {hasMore && (
          <div className="p-3 text-center">
            <Button variant="outline" size="sm" onClick={onLoadMore}>
              Load more
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

const ROW_ACTION_UI: Record<RowAction, { title: string; icon: typeof Check; hover: string }> = {
  hide: { title: 'Hide this comment', icon: EyeOff, hover: 'hover:border-slate-300 hover:text-slate-700' },
  delete: { title: 'Delete this comment', icon: Trash2, hover: 'hover:border-red-300 hover:text-red-600' },
  resolve: { title: 'Mark resolved', icon: Check, hover: 'hover:border-kyck-300 hover:text-kyck-600' },
};

function RowActions({
  actions,
  onAction,
}: {
  actions: RowAction[];
  onAction: (action: RowAction) => void;
}) {
  if (actions.length === 0) return null;
  return (
    <div className="absolute right-2 top-1/2 hidden -translate-y-1/2 items-center gap-1 group-hover:flex group-focus-within:flex">
      {actions.map((action) => {
        const ui = ROW_ACTION_UI[action];
        const Icon = ui.icon;
        return (
          <button
            key={action}
            type="button"
            title={ui.title}
            aria-label={ui.title}
            onClick={(e) => {
              e.stopPropagation();
              onAction(action);
            }}
            className={cn(
              'flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400 shadow-sm',
              ui.hover,
            )}
          >
            <Icon className="h-4 w-4" />
          </button>
        );
      })}
    </div>
  );
}
